/**
 * Unified Animation Controller
 * Single system for managing all avatar animations through Three.js AnimationMixer
 * Replaces dual animation systems (mixer + requestAnimationFrame loops)
 *
 * Nuamedia Solutions Pty Ltd - January 2026
 */

import * as THREE from 'three';
import { AnimationPriority, AnimationType, getPriorityForType } from '../utils/animationUtils';
import { AnimationStateManager } from './AnimationStateManager';

// Morph targets that belong to mouth/speech - speaking gets exclusive control
const MOUTH_MORPHS = new Set([
  'jawOpen', 'jawForward', 'jawLeft', 'jawRight', 'jaw_L', 'jaw_R',
  'mouthOpen', 'mouthClose', 'mouthFunnel', 'mouthPucker',
  'mouthUpperUp_L', 'mouthUpperUp_R', 'mouthLowerDown_L', 'mouthLowerDown_R',
  'mouthSmile_L', 'mouthSmile_R', 'mouthFrown_L', 'mouthFrown_R',
  'mouthStretch_L', 'mouthStretch_R', 'mouthDimple_L', 'mouthDimple_R',
  'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower', 'mouthShrugUpper',
  'mouthPress_L', 'mouthPress_R', 'mouthLeft', 'mouthRight',
  'tongueOut', 'cheekPuff', 'cheekSquint_L', 'cheekSquint_R'
]);

// Animation weights by priority
const ANIMATION_WEIGHTS: Record<AnimationType, number> = {
  [AnimationType.SPEAKING]: 1.0,
  [AnimationType.THINKING]: 0.8,
  [AnimationType.LISTENING]: 0.6,
  [AnimationType.IDLE]: 0.3
};

// Transition durations in seconds
const FADE_IN_DURATION = 0.2;
const FADE_OUT_DURATION = 0.1;

export interface AnimationEntry {
  action: THREE.AnimationAction;
  type: AnimationType;
  priority: AnimationPriority;
  clip: THREE.AnimationClip;
  isPlaying: boolean;
  targetWeight: number;
  currentWeight: number;
}

export interface UnifiedControllerOptions {
  mixer: THREE.AnimationMixer;
  onAnimationComplete?: (type: AnimationType) => void;
  onAnimationStart?: (type: AnimationType) => void;
}

export class UnifiedAnimationController {
  private mixer: THREE.AnimationMixer;
  private activeAnimations: Map<string, AnimationEntry> = new Map();
  private currentSpeakingAnimation: AnimationEntry | null = null;
  private onAnimationComplete?: (type: AnimationType) => void;
  private onAnimationStart?: (type: AnimationType) => void;

  // Track if speaking is active (for priority enforcement)
  private isSpeaking: boolean = false;

  // Frame timing for performance
  private lastUpdateTime: number = 0;
  private readonly MIN_UPDATE_INTERVAL = 0.016; // ~60fps

  constructor(options: UnifiedControllerOptions) {
    this.mixer = options.mixer;
    this.onAnimationComplete = options.onAnimationComplete;
    this.onAnimationStart = options.onAnimationStart;

    // Listen for animation finished events
    this.mixer.addEventListener('finished', this.handleAnimationFinished.bind(this));
  }

  /**
   * Start an animation with the given clip
   * Priority is determined by animation type
   */
  startAnimation(
    type: AnimationType,
    clip: THREE.AnimationClip,
    options: {
      loop?: THREE.AnimationActionLoopStyles;
      clampWhenFinished?: boolean;
      fadeIn?: boolean;
      id?: string; // Optional ID for tracking multiple animations of same type
    } = {}
  ): THREE.AnimationAction | null {
    const {
      loop = THREE.LoopOnce,
      clampWhenFinished = true,
      fadeIn = true,
      id = `${type}-${Date.now()}`
    } = options;

    const priority = getPriorityForType(type);
    const targetWeight = ANIMATION_WEIGHTS[type];

    // If speaking, stop all other animations that use mouth morphs
    if (type === AnimationType.SPEAKING) {
      this.stopMouthAnimations();
      this.isSpeaking = true;
    } else if (this.isSpeaking) {
      // Speaking is active - check if this animation should be allowed
      // Allow idle animations but at reduced weight (they don't affect mouth)
      if (type !== AnimationType.IDLE) {
        console.log(`[UnifiedAnimationController] Skipping ${type} - speaking in progress`);
        return null;
      }
      // Allow idle to proceed (it doesn't control mouth morphs)
    }

    // Create the action
    const action = this.mixer.clipAction(clip);
    action.setLoop(loop, loop === THREE.LoopOnce ? 1 : Infinity);
    action.clampWhenFinished = clampWhenFinished;

    // Set initial weight
    if (fadeIn) {
      action.setEffectiveWeight(0);
    } else {
      action.setEffectiveWeight(targetWeight);
    }

    action.reset();
    action.play();

    // Store animation entry
    const entry: AnimationEntry = {
      action,
      type,
      priority,
      clip,
      isPlaying: true,
      targetWeight,
      currentWeight: fadeIn ? 0 : targetWeight
    };

    this.activeAnimations.set(id, entry);

    // Track speaking animation separately for quick access
    if (type === AnimationType.SPEAKING) {
      this.currentSpeakingAnimation = entry;
    }

    console.log(`[UnifiedAnimationController] Started ${type} animation (id: ${id}, weight: ${targetWeight})`);

    this.onAnimationStart?.(type);

    return action;
  }

  /**
   * Stop an animation by type or ID
   */
  stopAnimation(typeOrId: AnimationType | string, fadeOut: boolean = true): void {
    // If stopping by type, stop all animations of that type
    const idsToStop: string[] = [];

    this.activeAnimations.forEach((entry, id) => {
      if (entry.type === typeOrId || id === typeOrId) {
        idsToStop.push(id);
      }
    });

    idsToStop.forEach(id => {
      const entry = this.activeAnimations.get(id);
      if (entry) {
        if (fadeOut) {
          entry.targetWeight = 0;
          entry.isPlaying = false;
        } else {
          entry.action.stop();
          this.activeAnimations.delete(id);
        }
      }
    });

    // If stopping speaking, allow other animations again
    if (typeOrId === AnimationType.SPEAKING) {
      this.isSpeaking = false;
      this.currentSpeakingAnimation = null;
    }

    console.log(`[UnifiedAnimationController] Stopped ${typeOrId} animation(s)`);
  }

  /**
   * Stop all animations that affect mouth morphs (for speaking takeover)
   */
  private stopMouthAnimations(): void {
    this.activeAnimations.forEach((entry, id) => {
      if (entry.type !== AnimationType.SPEAKING) {
        // Check if this animation affects mouth morphs
        const affectsMouth = entry.clip.tracks.some(track => {
          const propName = track.name.split('.').pop() || '';
          return MOUTH_MORPHS.has(propName);
        });

        if (affectsMouth) {
          entry.action.stop();
          this.activeAnimations.delete(id);
          console.log(`[UnifiedAnimationController] Stopped ${entry.type} (conflicts with speaking)`);
        }
      }
    });
  }

  /**
   * Update all animations - called every frame from useFrame
   * Handles weight transitions and priority enforcement
   */
  update(delta: number): void {
    // Clamp delta to prevent large jumps (e.g., after tab switch)
    const clampedDelta = Math.min(delta, 0.033); // Max ~30fps worth of time

    // Skip if too soon since last update (performance optimization)
    this.lastUpdateTime += clampedDelta;
    if (this.lastUpdateTime < this.MIN_UPDATE_INTERVAL) {
      return;
    }
    const actualDelta = this.lastUpdateTime;
    this.lastUpdateTime = 0;

    // Update animation weights (fade in/out)
    this.activeAnimations.forEach((entry, id) => {
      if (entry.currentWeight !== entry.targetWeight) {
        const direction = entry.targetWeight > entry.currentWeight ? 1 : -1;
        const duration = direction > 0 ? FADE_IN_DURATION : FADE_OUT_DURATION;
        const weightChange = actualDelta / duration;

        entry.currentWeight += direction * weightChange;

        // Clamp to target
        if (direction > 0) {
          entry.currentWeight = Math.min(entry.currentWeight, entry.targetWeight);
        } else {
          entry.currentWeight = Math.max(entry.currentWeight, entry.targetWeight);
        }

        entry.action.setEffectiveWeight(entry.currentWeight);

        // Remove if faded out completely
        if (entry.currentWeight <= 0 && !entry.isPlaying) {
          entry.action.stop();
          this.activeAnimations.delete(id);
        }
      }
    });

    // Update the mixer
    this.mixer.update(actualDelta);
  }

  /**
   * Handle animation finished event from mixer
   */
  private handleAnimationFinished(event: THREE.Event): void {
    const finishedAction = (event as any).action as THREE.AnimationAction;

    // Find the animation entry for this action
    let foundEntry: { entry: AnimationEntry; id: string } | null = null;

    // Convert to array for TypeScript compatibility
    const entries = Array.from(this.activeAnimations.entries());
    for (let i = 0; i < entries.length; i++) {
      const [id, entry] = entries[i];
      if (entry.action === finishedAction) {
        foundEntry = { entry, id };
        break;
      }
    }

    if (foundEntry) {
      const { entry, id } = foundEntry;
      const type = entry.type;

      // Remove from active animations
      this.activeAnimations.delete(id);

      // If speaking finished, allow other animations
      if (type === AnimationType.SPEAKING) {
        this.isSpeaking = false;
        this.currentSpeakingAnimation = null;
      }

      console.log(`[UnifiedAnimationController] Animation finished: ${type}`);
      this.onAnimationComplete?.(type);
    }
  }

  /**
   * Check if speaking animation is currently active
   */
  isSpeakingActive(): boolean {
    return this.isSpeaking;
  }

  /**
   * Get the current speaking animation entry (if any)
   */
  getCurrentSpeakingAnimation(): AnimationEntry | null {
    return this.currentSpeakingAnimation;
  }

  /**
   * Check if any animation of the given type is active
   */
  isAnimationActive(type: AnimationType): boolean {
    const entries = Array.from(this.activeAnimations.values());
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (entry.type === type && entry.isPlaying) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get all active animation types
   */
  getActiveTypes(): AnimationType[] {
    const types = new Set<AnimationType>();
    this.activeAnimations.forEach(entry => {
      if (entry.isPlaying) {
        types.add(entry.type);
      }
    });
    return Array.from(types);
  }

  /**
   * Stop all animations immediately
   */
  stopAll(): void {
    this.activeAnimations.forEach((entry, id) => {
      entry.action.stop();
    });
    this.activeAnimations.clear();
    this.isSpeaking = false;
    this.currentSpeakingAnimation = null;
    console.log('[UnifiedAnimationController] All animations stopped');
  }

  /**
   * Dispose of the controller and clean up
   */
  dispose(): void {
    this.stopAll();
    this.mixer.removeEventListener('finished', this.handleAnimationFinished.bind(this));
  }
}

// Animation controller instances keyed by scope ID ('' = default global)
const controllerInstances = new Map<string, UnifiedAnimationController>();

export function getUnifiedAnimationController(options?: UnifiedControllerOptions, instanceId = ''): UnifiedAnimationController | null {
  if (options) {
    const existing = controllerInstances.get(instanceId);
    if (existing) {
      existing.dispose();
    }
    const controller = new UnifiedAnimationController(options);
    controllerInstances.set(instanceId, controller);
    return controller;
  }
  return controllerInstances.get(instanceId) ?? null;
}

export function resetUnifiedAnimationController(instanceId = ''): void {
  const controller = controllerInstances.get(instanceId);
  if (controller) {
    controller.dispose();
    controllerInstances.delete(instanceId);
  }
}

// Animation state managers keyed by scope ID
const animationStateManagers = new Map<string, AnimationStateManager>();

export function getGlobalAnimationStateManager(instanceId = ''): AnimationStateManager {
  let manager = animationStateManagers.get(instanceId);
  if (!manager) {
    manager = new AnimationStateManager();
    animationStateManagers.set(instanceId, manager);
  }
  return manager;
}

/**
 * Animation Controller - Simplified adapter for RAF-based animations
 *
 * SINGLE SOURCE OF TRUTH: Uses AnimationStateManager for all speaking state checks.
 * This eliminates the race conditions caused by multiple speaking flags.
 *
 * Responsibilities:
 * - Track requestAnimationFrame IDs for cleanup
 * - Delegate isSpeaking() to AnimationStateManager
 * - Provide immediate speaking takeover for lip-sync
 */
class AnimationControllerAdapter {
  private static instance: AnimationControllerAdapter | null = null;

  private activeRafIds: Set<number> = new Set();
  private instanceId: string;

  constructor(instanceId = '') {
    this.instanceId = instanceId;
  }

  static getInstance(instanceId = ''): AnimationControllerAdapter {
    if (!AnimationControllerAdapter.instance) {
      AnimationControllerAdapter.instance = new AnimationControllerAdapter(instanceId);
    }
    return AnimationControllerAdapter.instance;
  }

  isSpeaking(): boolean {
    return getGlobalAnimationStateManager(this.instanceId).isSpeakingActive();
  }

  immediateSpeakingTakeover(): void {
    const stateManager = getGlobalAnimationStateManager(this.instanceId);
    stateManager.startAnimation(AnimationType.SPEAKING, true, true);

    const controller = controllerInstances.get(this.instanceId);
    if (controller) {
      controller.stopAnimation(AnimationType.LISTENING, false);
      controller.stopAnimation(AnimationType.THINKING, false);
    }
  }

  endSpeaking(): void {
    getGlobalAnimationStateManager(this.instanceId).stopAnimation(AnimationType.SPEAKING, true);
  }

  setSpeaking(speaking: boolean): void {
    const stateManager = getGlobalAnimationStateManager(this.instanceId);
    if (speaking) {
      stateManager.startAnimation(AnimationType.SPEAKING, true, true);
    } else {
      stateManager.stopAnimation(AnimationType.SPEAKING, true);
    }
  }

  trackRafId(rafId: number): void {
    this.activeRafIds.add(rafId);
  }

  untrackRafId(rafId: number): void {
    this.activeRafIds.delete(rafId);
    cancelAnimationFrame(rafId);
  }

  cancelAllRafIds(): void {
    this.activeRafIds.forEach(id => {
      cancelAnimationFrame(id);
    });
    this.activeRafIds.clear();
  }

  reset(): void {
    getGlobalAnimationStateManager(this.instanceId).reset();
    this.cancelAllRafIds();
    resetUnifiedAnimationController(this.instanceId);
  }
}

export const animationController = AnimationControllerAdapter.getInstance();

/**
 * Dispose all animation singletons for an instance scope.
 * Call on avatar unmount to release resources and prevent cross-instance interference.
 */
export function disposeAnimationSingletons(instanceId = ''): void {
  resetUnifiedAnimationController(instanceId);
  const stateManager = animationStateManagers.get(instanceId);
  if (stateManager) {
    stateManager.reset();
    animationStateManagers.delete(instanceId);
  }
  animationController.cancelAllRafIds();
}
